import type { CreateMedicationsBulk } from "../../../api";
import type { MedicationDto } from "../../../api/api-connector";

import { medicationCreatePayload } from "./medication-payload";

/**
 * The create payload that reproduces an existing medication with corrections
 * applied. Used to fix a wrong medication group, which the update endpoint
 * cannot do — `medication_group` is the discriminator on create and isn't part
 * of `UpdateDefaultMedicationSchema` at all — so the record has to be created
 * anew and the mistaken one deleted.
 *
 * Everything the user would otherwise have to write down and retype is carried
 * over: dates, dose, unit, dosage, both frequency forms — the repeat unit
 * included, since dropping it would turn "every 2 months" into "every 2 days" —
 * and the comment. Server-owned fields (uuid, timestamps, history, study
 * visits) are left behind, as is `user_uuid`: the record is created by whoever
 * corrects it. `medication_change_auto_event` is `false` — correcting a
 * mistaken entry is not a change in therapy and must not log one.
 */
export function recreateMedicationPayload(
  item: MedicationDto,
  patch: Partial<MedicationDto>,
): CreateMedicationsBulk["medications"][number] {
  const corrected = { ...item, ...patch };

  return medicationCreatePayload({
    medication_group: corrected.medication_group,
    medication_name: corrected.medication_name,
    medication_started: corrected.medication_started ?? null,
    medication_ended: corrected.medication_ended ?? null,
    medication_dose: corrected.medication_dose,
    medication_unit: corrected.medication_unit,
    medication_dosage: corrected.medication_dosage,
    medication_frequency_regular_repeat_every:
      corrected.medication_frequency_regular_repeat_every ?? null,
    medication_frequency_regular_repeat_every_unit:
      corrected.medication_frequency_regular_repeat_every_unit,
    medication_frequency_on_days:
      corrected.medication_frequency_on_days ?? null,
    comment: corrected.comment ?? null,
    patient_id: corrected.patient_id,
    medication_change_auto_event: false,
  });
}
